import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import { useLanguage } from '../contexts/LanguageContext';

const dimensions = [
  ['task', '任務完成', 'Task completion'],
  ['grammar', '文法', 'Grammar'],
  ['vocabulary', '詞彙', 'Vocabulary'],
  ['fluency', '流暢度', 'Fluency'],
];

const gradeColor = (grade) => {
  if (grade === 'A') return 'success';
  if (grade === 'B') return 'primary';
  if (grade === 'C') return 'warning';
  return 'error';
};

export function EnglishReviewContent({ review }) {
  const { tr } = useLanguage();
  if (!review) return null;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          color: '#fff',
          background: 'linear-gradient(135deg,#123d78,#1976d2)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8 }}>CEFR {review.level}</Typography>
            <Typography variant="h3" fontWeight={900}>{review.total_score ?? 0}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>{tr('英文能力總分', 'Overall English score')}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight={800}>{review.completion_rate ?? 0}%</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>{tr('任務完成度', 'Task completion')}</Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        {dimensions.map(([key, zhLabel, enLabel]) => (
          <Grid item xs={6} md={3} key={key}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight={800}>{tr(zhLabel, enLabel)}</Typography>
                <Chip
                  label={review.grades?.[key] || '—'}
                  color={gradeColor(review.grades?.[key])}
                  size="small"
                  sx={{ fontWeight: 900 }}
                />
              </Box>
              <Typography variant="h5" fontWeight={900} sx={{ mt: 1 }}>
                {review.scores?.[key] ?? 0}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900}>{tr('整體講評', 'Overall feedback')}</Typography>
        <Typography sx={{ mt: 1, lineHeight: 1.8 }}>{review.overall}</Typography>
        {review.grade_reason && (
          <Alert severity="info" icon={<TrendingUpIcon />} sx={{ mt: 2 }}>
            {review.grade_reason}
          </Alert>
        )}
      </Paper>

      {review.grammar?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={900}>{tr('原句改善', 'Improved sentences')}</Typography>
          {review.grammar.map((item, index) => (
            <Box key={`${item.original}-${index}`} sx={{ mt: 2, pl: 2, borderLeft: '3px solid #1976d2' }}>
              <Typography color="text.secondary" sx={{ textDecoration: 'line-through' }}>{item.original}</Typography>
              <Typography color="primary" fontWeight={800}>{item.better}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{item.note}</Typography>
            </Box>
          ))}
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>{tr('詞彙', 'Vocabulary')}</Typography>
            <Typography sx={{ mt: 1, lineHeight: 1.8 }}>{review.vocabulary}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>{tr('流暢度', 'Fluency')}</Typography>
            <Typography sx={{ mt: 1, lineHeight: 1.8 }}>{review.fluency}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900}>{tr('任務回饋', 'Task feedback')}</Typography>
        <Typography sx={{ mt: 1, lineHeight: 1.8 }}>{review.task_feedback}</Typography>
      </Paper>

      {review.vocab_list?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={900}>{tr('本次實用詞彙', 'Useful vocabulary from this session')}</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {review.vocab_list.map((item, index) => (
              <Chip key={`${item.word}-${index}`} label={tr(`${item.word}｜${item.meaning}`, `${item.word} – ${item.meaning}`)} variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      {review.next_steps?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={900}>{tr('下次練習', 'What to practice next')}</Typography>
          {review.next_steps.map((step, index) => (
            <Box key={`${step}-${index}`} sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
              <Chip label={index + 1} size="small" color="primary" />
              <Typography>{step}</Typography>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

export function EnglishReviewDialog({ open, onClose, review }) {
  const { tr } = useLanguage();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {tr('英文口說講評', 'English Speaking Review')}
        <IconButton onClick={onClose} aria-label={tr('關閉英文講評', 'Close English review')}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ bgcolor: '#f6f8fc' }}>
        <EnglishReviewContent review={review} />
      </DialogContent>
    </Dialog>
  );
}
